<template>
  <div class="task-list panel list-panel">
    <el-card class="box-card">
      <div slot="header" class="clearfix">
        <div class="pull-right notification-count" v-if="newTasksCount > 0">
          <icon scale="1.2" name="star"></icon> <span>{{ newTasksCount }} NEW TASK{{ newTasksCount > 1 ? 'S' : '' }}</span>
        </div>
        <span><b>{{ title }}</b></span>
        <week-total-tags v-if="showWeekTotals" :user="user" size="small" />
      </div>
      <draggable v-if="sortable"
                 v-model="tasksSorted"
                 :options="{group: 'tasks', delay: 5, handle: '.drag-handle'}"
                 @end="updateSortOrder"
                 class="task-list-group list-group">
        <task-list-item v-for="task in tasksSorted"
                        :key="task.id"
                        :show-client-code="showClientCode"
                        :show-status-tag="showStatusTag"
                        :show-start-button="showStartButton"
                        :show-task-project-name="showTaskProjectName"
                        :task="task"
                        :project="project"
                        :user="user" />
      </draggable>

      <div class="task-list-group list-group no-sort"
            v-if="!sortable">
        <task-list-item v-for="task in tasksSorted"
                        :key="task.id"
                        :show-client-code="showClientCode"
                        :show-status-tag="showStatusTag"
                        :show-start-button="showStartButton"
                        :show-task-project-name="showTaskProjectName"
                        :task="task"
                        :project="project"
                        :user="user" />
      </div>

      <div class="task-list-item list-item" v-if="!tasks.length">
        <p>No Tasks Found!</p>
      </div>
    </el-card>
  </div>
</template>

<script>
  import draggable from 'vuedraggable'

  export default {
    name: 'task-list',
    components: {draggable},
    props: {
      showClientCode: {
        default: false
      },
      showStatusTag: { default: false },
      showStartButton: { default: false },
      showTaskProjectName: { default: false },
      title: {
        default: 'Tasks'
      },
      showWeekTotals: {
        default: false
      },
      sortable: { default: false },
      tasks: null,
      project: null, // Scope the Tasks to a single project. Also affects the TaskCRUD default Project Selection
      user: null // Scope the Tasks to a single user. Hides the user line on a task
    },
    data () {
      return {
        sortInProgress: false, // Prevent Sort updates from re-triggering renders before the sort has completed
        tasksSorted: [] // Will be set on created(), and when state changes
      }
    },
    methods: {
      rebuildSortedTaskList () {
        if (this.user) {
          let userSortOrder = this.user.task_sort || [] // User sort order - if defined yet
          this.tasksSorted = this.tasks.sort((a, b) => {
            // Check to see if the User has any sort preference
            if (userSortOrder.indexOf(a.id) > -1 && userSortOrder.indexOf(b.id) > -1) {
              return userSortOrder.indexOf(a.id) - userSortOrder.indexOf(b.id)
            }

            // Default sort: Group by Client, project, created_at
            if (a.client_name > b.client_name) return 1
            if (a.client_name < b.client_name) return -1

            if (a.project_code > b.project_code) return 1
            if (a.project_code < b.project_code) return -1

            if (a.created_at > b.created_at) return -1
            if (a.created_at < b.created_at) return 1
          })
        } else {
          // We dont want to sort anything, just return tasks as is
          this.tasksSorted = this.tasks.sort((a, b) => {
            // Default sort created_at desc
            if (a.created_at > b.created_at) return -1
            if (a.created_at < b.created_at) return 1
          })
        }
      },
      updateSortOrder () {
        let sortOrder = []
        // Rebuild the sort order
        for (let i in this.tasksSorted) {
          sortOrder.push(this.tasksSorted[i].id)
        }
        let updatedUser = {
          id: this.user.id,
          task_sort: sortOrder
        }

        // Lock the task sort. Will also unlock in the updatedUser watcher below
        clearTimeout(this.sortInProgress)
        this.sortInProgress = true
        this.sortInProgress = setTimeout(function () {
          this.sortInProgress = false
        }.bind(this), 5000)

        // Send the update to the API
        this.$store.dispatch('updateUser', updatedUser)
      }
    },
    created () {
      this.rebuildSortedTaskList()
    },
    mounted () {
      if (this.user && this.user.id === this.currentUser.id) {
        this.$store.dispatch('getNotifications')
      }
    },
    computed: {
      newTasksCount () {
        // Count the number of new TASK notifications this user has in this group of tasks
        let count = 0

        let taskIds = this.tasks.map(item => {
          return item.id
        })

        for (let k in this.notifications) {
          if (this.notifications.hasOwnProperty(k) &&
            this.notifications[k].user_id === this.currentUser.id &&
            this.notifications[k].task_id !== null &&
            taskIds.indexOf(this.notifications[k].task_id) > -1) {
            count++
          }
        }
        return count
      },

      // Vuex Mappings
      allTasks () {
        return this.$store.state.tasks.objects
      },
      notifications () {
        return this.$store.state.notifications.objects
      },
      currentUser () {
        return this.$store.state.users.currentUser
      },
      updatedUser () {
        return this.$store.state.users.updatedObject
      }
    },
    watch: {
      'updatedUser' () {
        // User likely resorted tasks and the action completed, unlock the sort in progress
        if (this.updatedUser && this.updatedUser.id === this.user.id) {
          this.sortInProgress = false
        }
      },
      'tasks' () {
        // When a new task is added/removed, update our task list
        if (!this.sortInProgress) {
          this.rebuildSortedTaskList()
        }
      }
    }
  }
</script>

<style lang="less" rel="stylesheet/less">
  .task-list {
    .sortable-chosen {
      transition: box-shadow 0.3s;
      box-shadow: 0 2px 5px #AAA;
    }

    .notification-count {
      color: #FDBD10;
      font-weight: bold;

      .fa-icon {
        vertical-align: middle;
        margin-right: 4px;
      }
      span {
        font-size: 1.1rem;
        vertical-align: middle;
      }
    }

    .no-sort {
      .task-list-item {
        padding-left: 15px;
      }
      .drag-handle {
        display: none;
      }
    }
  }
  .task-list-group {
    padding: 5px;
  }
</style>
